import type { ProductionOrderStatus, ProductionOrderSummary } from '@erp/shared';
import { Factory, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';
import { formatMoney, trimMoney } from '../../lib/money.js';

const STATUS_TONE: Record<ProductionOrderStatus, 'success' | 'secondary' | 'danger' | 'default'> = {
  posted: 'success',
  draft: 'secondary',
  voided: 'danger',
  cancelled: 'default',
};

/**
 * Production orders — every make (and un-make) the shop has recorded.
 * Creating an order moves nothing; the stock moves when it is POSTED, on the
 * detail page or straight from the form's "save & post".
 */
export function ProductionOrdersPage(): JSX.Element {
  const { t, i18n } = useTranslation('manufacturing');
  const navigate = useNavigate();
  const canWrite = usePermission('manufacturing.write');
  const [rows, setRows] = useState<ProductionOrderSummary[] | null>(null);

  useEffect(() => {
    void api().get<{ items: ProductionOrderSummary[] }>('/manufacturing/orders', { params: { limit: 500 } })
      .then((r) => setRows(r.data.items))
      .catch(() => setRows([]));
  }, []);

  if (!rows) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;

  const columns: DataTableColumn<ProductionOrderSummary>[] = [
    {
      id: 'docNumber',
      header: t('orders.number', { defaultValue: 'Number' }),
      cell: (r) => <span className="font-medium">{r.docNumber}</span>,
      sortValue: (r) => r.docNumber,
      searchValue: (r) => r.docNumber,
    },
    {
      id: 'kind',
      header: t('orders.kind', { defaultValue: 'Type' }),
      cell: (r) => t(`orders.kinds.${r.kind}`, { defaultValue: r.kind }),
      sortValue: (r) => r.kind,
    },
    {
      id: 'item',
      header: t('orders.product', { defaultValue: 'Product' }),
      cell: (r) => (
        <span>
          <span className="font-mono text-xs text-fg-muted">{r.itemCode}</span> · {r.itemName}
        </span>
      ),
      sortValue: (r) => r.itemCode,
      searchValue: (r) => `${r.itemCode} ${r.itemName}`,
    },
    {
      id: 'qty',
      header: t('orders.qty', { defaultValue: 'Qty' }),
      align: 'end',
      cell: (r) => <span className="font-mono">{trimMoney(r.quantity)}{r.unitName ? ` ${r.unitName}` : ''}</span>,
      sortValue: (r) => Number(r.quantity),
    },
    {
      id: 'unitCost',
      header: t('orders.unitCost', { defaultValue: 'Unit cost' }),
      align: 'end',
      cell: (r) => (r.unitCost && r.costCurrencyCode
        ? <span className="font-mono">{formatMoney(r.unitCost, { locale: i18n.language, currency: r.costCurrencyCode })}</span>
        : '—'),
      sortValue: (r) => (r.unitCost ? Number(r.unitCost) : null),
    },
    {
      id: 'status',
      header: t('orders.status', { defaultValue: 'Status' }),
      cell: (r) => <Badge variant={STATUS_TONE[r.status]}>{t(`orders.state.${r.status}`, { defaultValue: r.status })}</Badge>,
      sortValue: (r) => r.status,
    },
    {
      id: 'createdAt',
      header: t('orders.created', { defaultValue: 'Created' }),
      cell: (r) => formatDateTime(r.createdAt, { locale: i18n.language }),
      sortValue: (r) => r.createdAt,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Factory className="h-6 w-6 text-brand" aria-hidden />
            {t('orders.title', { defaultValue: 'Production orders' })}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t('orders.subtitle', { defaultValue: 'Make products from raw materials — or take them back apart. Stock moves when an order is posted.' })}
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => navigate('/manufacturing/orders/new')}>
            <Plus className="me-1.5 h-4 w-4" aria-hidden />
            {t('orders.new', { defaultValue: 'New production order' })}
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={<Factory className="h-8 w-8" aria-hidden />}
          title={t('orders.empty', { defaultValue: 'Nothing made yet' })}
          description={t('orders.emptyHint', { defaultValue: 'Give a product a recipe on its Recipe tab, then make it from here.' })}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            data={rows}
            columns={columns}
            rowKey={(r) => r.id}
            searchable
            onRowClick={(r) => navigate(`/manufacturing/orders/${r.id}`)}
            initialSort={{ id: 'createdAt', dir: 'desc' }}
          />
        </Card>
      )}
    </div>
  );
}
